import { useTranslation } from 'react-i18next'
import { ModalPortal } from '../../../../common/components/Portal/Portal'
import { CloseButton } from '../../../../common/components/button/CloseButton'
import { getIcon } from '../../../../common/components/icons/utils'
import { useFadeAnimation } from '../../../../hooks/FadeAnimation/useFadeAnimation'
import { useDisableBodyScroll } from '../../../../hooks/useDisableBodyScroll'
import { useTrapFocus } from '../../../../hooks/useTrapFocus'
import { MemberRole } from '../../../../types/commonTypes'
import styles from './AddMembersModal.module.css'
import { AddMembers } from './AddMembers'
import { AddMembersPreview } from './AddMembersPreview'
import { useState } from 'react'
import { ThreeDots } from '../../../../common/components/loaders/ThreeDots/ThreeDots'
import { LoaderWithOverlay } from '../../../../composites/GamesOverview/components/LoaderWithOverlay'
import classNames from 'classnames'

export interface Member {
  name: string
  email: string
  role: MemberRole
  orgId: string
  saigeAccess: boolean
}

type AddMembersModalProps = {
  organizationName: string
  onSubmit: (values: AddMembersFormValues) => Promise<boolean>
  onClose: () => void
  memberCount: number
  maxMembers: number
  limitMembers: boolean
  orgHasSaigeAccess: boolean
}

export type AddMembersFormValues = {
  members: Member[]
  message1: string
  message2: string
}

export const AddMembersModal: React.FC<AddMembersModalProps> = ({
  onSubmit,
  organizationName,
  onClose,
  memberCount,
  maxMembers,
  limitMembers,
  orgHasSaigeAccess,
}) => {
  const { t } = useTranslation()
  const trapFocusRef = useTrapFocus<HTMLDivElement>()
  useDisableBodyScroll()
  const [values, setValues] = useState<AddMembersFormValues | null>(null)
  const [showPreview, setShowPreview] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const originalMessage1 = t(
    'businesses.members.invite_email.default.message_beginning',
    'Welcome to use the Seppo gamification platform. \n\nTo start using Seppo, go to https://play.seppo.io -> Create games.\n\n',
  )
  const originalMessage2 = t(
    'businesses.members.invite_email.default.message_end',
    'We recommend using Chrome, Safari or Firefox. \n\nOnce you have logged in you can\n- Get started with creating Seppo games\n- Find support material and contact our customer support from Help\n- Reset your password from settings\n\nEnjoy the magic of games!\n\nBest,\n\nSeppo team',
  )

  const { fadeTransition, closeWithDelay } = useFadeAnimation()

  const onSubmitInternal = async (message1: string, message2: string) => {
    setSubmitting(true)
    if (values) {
      const newValues: AddMembersFormValues = {
        members: values?.members,
        message1,
        message2,
      }
      const isSuccess = await onSubmit(newValues)
      if (isSuccess) {
        onCloseInternal()
      }
    }
    setSubmitting(false)
  }

  const onCloseInternal = () => {
    closeWithDelay(350, onClose)
  }

  const handleSetValues = (member: Member) => {
    setValues((prevValues) =>
      prevValues
        ? { message1: prevValues.message1, message2: prevValues.message2, members: [...prevValues.members, member] }
        : { message1: '', message2: '', members: [member] },
    )
  }

  const onPreview = () => {
    setShowPreview(true)
  }

  const onBack = () => {
    setShowPreview(false)
  }

  return (
    <ModalPortal>
      <div className={fadeTransition}>
        <div className={styles.backdrop}>
          <div className={classNames(styles.container, orgHasSaigeAccess && styles.containerSaige)} ref={trapFocusRef}>
            <div className={styles.content}>
              {submitting && (
                <LoaderWithOverlay>
                  <ThreeDots />
                </LoaderWithOverlay>
              )}
              <div className={styles.header}>
                <div className={styles.headerTitle}>
                  <span>{getIcon('settingsGradient')}</span>
                  <span className='medium'>
                    {t('settings_organization.members.add_members.title', {
                      organization_name: organizationName,
                      defaultValue: "Add members to '%{organization_name}' organization",
                    })}
                  </span>
                </div>
                <CloseButton autoFocus onClick={onCloseInternal} />
              </div>
              {!showPreview && (
                <AddMembers
                  onPreview={onPreview}
                  onClose={onCloseInternal}
                  values={values}
                  setAllValues={setValues}
                  setValue={handleSetValues}
                  memberCount={memberCount + (values ? values.members.length : 1)}
                  maxMembers={maxMembers}
                  limitMembers={limitMembers}
                  orgHasSaigeAccess={orgHasSaigeAccess}
                />
              )}
              {showPreview && (
                <AddMembersPreview
                  onSubmit={onSubmitInternal}
                  onClose={onCloseInternal}
                  onBack={onBack}
                  infomessage={{
                    key: 'settings_organization.members.add_members_preview.description',
                    default:
                      'Adding members to your organisation allows them to create and share content. <br/>They will receive an invitation email after you have added them.',
                  }}
                  originalMessage1={originalMessage1}
                  originalMessage2={originalMessage2}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  )
}
